import React, { useCallback, useEffect, useMemo } from 'react'
import { Tabs, Tab } from '@material-ui/core'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Link } from '@reach/router'
import { useSelector, useDispatch } from 'react-redux'

import {
  getServiceOrderByID,
  currentServiceOrderSelector,
  getServiceOrderByIdLoadingSelector,
} from '_modules/service-orders/selectors'
import { getServiceOrder, setCurrent } from '_modules/service-orders/actions'
import { ROUTES } from '_utils/constants'

import useStyles from './styles'
import { getHistoryLogs } from '_modules/history-logs/actions'

const getServiceOrderPath = id => `${ROUTES.SERVICE_ORDER}/${id}`

const isOpeningInNewTab = event =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1

const isTabSelected = (pathname, id) => pathname?.split('/').some(value => value === id?.toString())

const DuplicatedTabs = ({ serviceOrderId, pathname }) => {
  const dispatch = useDispatch()
  const currentServiceOrder = useSelector(currentServiceOrderSelector)
  const isGetServiceOrderLoading = useSelector(getServiceOrderByIdLoadingSelector)

  const originalServiceOrder = useSelector(state =>
    getServiceOrderByID(state, currentServiceOrder?.id?.toString())
  )

  const serviceOrder = useMemo(() => {
    if (!currentServiceOrder?.duplicatedFrom) {
      return currentServiceOrder
    }
    return originalServiceOrder
  }, [originalServiceOrder, currentServiceOrder])

  const styles = useStyles({})

  useEffect(() => {
    if (currentServiceOrder.duplicatedFrom && !isGetServiceOrderLoading && !originalServiceOrder) {
      dispatch(getServiceOrder(currentServiceOrder?.duplicatedFrom, false))
    }
  }, [dispatch, originalServiceOrder, currentServiceOrder.duplicatedFrom, isGetServiceOrderLoading])

  const handleTabClick = useCallback(
    id => event => {
      if (isOpeningInNewTab(event)) {
        return
      }

      dispatch(setCurrent(id.toString()))
      dispatch(getServiceOrder(id))
      dispatch(getHistoryLogs(id))
    },
    [dispatch]
  )

  const renderServiceOrderTab = useCallback(
    (id, { keyPrefix = 'tab' } = {}) => (
      <Tab
        key={`${keyPrefix}-${id}`}
        className={classnames(styles.tab, {
          [styles.selectedTab]: isTabSelected(pathname, id),
        })}
        value={`tab-${id}`}
        label={`Chamado #${id}`}
        component={Link}
        to={getServiceOrderPath(id)}
        onClick={handleTabClick(id)}
      />
    ),
    [handleTabClick, pathname, styles.selectedTab, styles.tab]
  )

  return (
    <Tabs
      classes={{ indicator: styles.selected }}
      value={`tab-${serviceOrderId}`}
      aria-label="Selecione o chamado"
      indicatorColor="primary"
      className={styles.tabs}
      variant="fullWidth"
    >
      {serviceOrder?.duplicatedFrom &&
        renderServiceOrderTab(serviceOrder.get('duplicatedFrom'), { keyPrefix: 'tab-original' })}
      {serviceOrder?.get('id') &&
        renderServiceOrderTab(serviceOrder.get('id'), { keyPrefix: 'tab-current' })}
      {serviceOrder?.serviceOrderDuplications?.map(item =>
        item?.get('id')
          ? renderServiceOrderTab(item.get('id'), { keyPrefix: 'tab-duplicate' })
          : null
      )}
    </Tabs>
  )
}

DuplicatedTabs.propTypes = {
  pathname: PropTypes.string,
  serviceOrderId: PropTypes.string,
}

DuplicatedTabs.defaultProps = {
  pathname: '',
  serviceOrderId: '',
}
export default React.memo(DuplicatedTabs)
